import React, { useMemo, useState } from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../SectionHeading';

// LIVE STORE: futureastronaut.myshopify.com. Branded shop WINDOW (Ryan, Aug 29):
// every product shot is background-removed via Cloudinary AI (far-shop-<key>,
// e_background_removal) and floats on a dark void tile with a per-category glow
// (apparel violet / hats cyan / accessories magenta — same hue system as the
// Records card headings). Category tabs wear the brand gradient when active.
// In the Webflow build this feeds from Shopify collections instead.

const STORE_URL = 'https://futureastronaut.myshopify.com';
const CLD = (key: string) => `https://res.cloudinary.com/kn1rv77n/image/upload/e_background_removal/f_auto,q_auto,w_320/far-shop-${key}.png`;
type Cat = 'all' | 'apparel' | 'hats' | 'acc';
const TABS: {
  key: Cat;
  label: string;
  dot?: string;
}[] = [{
  key: 'all',
  label: 'ALL'
}, {
  key: 'apparel',
  label: 'APPAREL',
  dot: '#8b5cff'
}, {
  key: 'hats',
  label: 'HATS',
  dot: '#37e5ff'
}, {
  key: 'acc',
  label: 'ACCESSORIES',
  dot: '#c86bff'
}];

// per-category glow behind the cut-out product
const GLOW: Record<string, string> = {
  apparel: 'rgba(139,92,255,0.30)',
  hats: 'rgba(55,229,255,0.20)',
  acc: 'rgba(200,107,255,0.26)'
};
const PRODUCTS = [{
  title: 'Basic Hoodie',
  price: '$60',
  handle: 'basic-hoodie',
  cat: 'apparel',
  key: 'hoodie'
}, {
  title: 'Textured Hoodie',
  price: '$75',
  handle: 'unisex-champion-tie-dye-hoodie',
  cat: 'apparel',
  key: 'texhoodie'
}, {
  title: 'Tie-Dye Tee',
  price: '$36',
  handle: 'oversized-tie-dye-t-shirt',
  cat: 'apparel',
  key: 'tiedyetee'
}, {
  title: 'Faded Tee',
  price: '$36',
  handle: 'oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'fadedtee'
}, {
  title: 'Alien Reflection Tee',
  price: '$36',
  handle: 'alien-reflection-oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'alientee'
}, {
  title: 'Performance Tee',
  price: '$29',
  handle: 'unisex-performance-crew-neck-t-shirt',
  cat: 'apparel',
  key: 'perftee'
}, {
  title: 'Baseball Jersey White',
  price: '$72',
  handle: 'baseball-jersey-white',
  cat: 'apparel',
  key: 'jerseyw'
}, {
  title: 'Baseball Jersey Black',
  price: '$72',
  handle: 'baseball-jersey-black',
  cat: 'apparel',
  key: 'jerseyb'
}, {
  title: 'Astronaut Bucket Hat',
  price: '$30',
  handle: 'astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'astrobucket'
}, {
  title: 'Alien Bucket Hat',
  price: '$32',
  handle: 'alien-pattern-bucket-hat',
  cat: 'hats',
  key: 'alienbucket'
}, {
  title: 'Alien Party Bucket Hat',
  price: '$25',
  handle: 'copy-of-astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'partybucket'
}, {
  title: 'AOP Bucket Hat',
  price: '$30',
  handle: 'bucket-hat-aop',
  cat: 'hats',
  key: 'aopbucket'
}, {
  title: 'Rocket Dad Hat',
  price: '$30',
  handle: 'dad-hat',
  cat: 'hats',
  key: 'rocketdad'
}, {
  title: 'OG Dad Hat',
  price: '$30',
  handle: 'og-logo-dad-hat',
  cat: 'hats',
  key: 'ogdad'
}, {
  title: 'Distressed Dad Hat',
  price: '$35',
  handle: 'distressed-metal-dat-hat',
  cat: 'hats',
  key: 'distdad'
}, {
  title: 'FAR Snapback',
  price: '$30',
  handle: 'flat-bill-cap-1',
  cat: 'hats',
  key: 'snapback'
}, {
  title: 'Flat Bill Cap',
  price: '$28',
  handle: 'flat-bill-cap',
  cat: 'hats',
  key: 'flatbill'
}, {
  title: 'Vintage Cap',
  price: '$30',
  handle: 'vintage-cap',
  cat: 'hats',
  key: 'vintage'
}, {
  title: 'Racing 5-Panel',
  price: '$30',
  handle: 'racing-design',
  cat: 'hats',
  key: 'racing5'
}, {
  title: 'FAR Rope Cap',
  price: '$25',
  handle: 'future-astronaut-cap-black',
  cat: 'hats',
  key: 'ropecap'
}, {
  title: 'Rocket Beanie',
  price: '$30',
  handle: 'embroidered-beanie',
  cat: 'hats',
  key: 'rocketbeanie'
}, {
  title: 'OG Beanie',
  price: '$30',
  handle: 'og-logo-beanie',
  cat: 'hats',
  key: 'ogbeanie'
}, {
  title: 'Space Bandana',
  price: '$25',
  handle: 'all-over-print-bandana',
  cat: 'acc',
  key: 'bandana'
}, {
  title: 'Fanny Pack',
  price: '$40',
  handle: 'fanny-pack',
  cat: 'acc',
  key: 'fanny'
}, {
  title: 'Drawstring Bag',
  price: '$20',
  handle: 'drawstring-bag',
  cat: 'acc',
  key: 'drawstring'
}, {
  title: 'Galaxy Scarf',
  price: '$35',
  handle: 'galaxy-scarf',
  cat: 'acc',
  key: 'galaxyscarf'
}, {
  title: 'Astrology Scarf',
  price: '$35',
  handle: 'astrology-scarf',
  cat: 'acc',
  key: 'astroscarf'
}, {
  title: 'Space Pashmina',
  price: '$35',
  handle: 'space-scarf',
  cat: 'acc',
  key: 'pashmina'
}, {
  title: 'Space Scarf 2',
  price: '$40',
  handle: 'space-scarf-2',
  cat: 'acc',
  key: 'spacescarf2'
}, {
  title: 'Astrology Shawl',
  price: '$62',
  handle: 'astrology-shawl',
  cat: 'acc',
  key: 'shawl'
}, {
  title: 'Racing Iron-On Patch',
  price: '$12',
  handle: 'racing-iron-on-patch',
  cat: 'acc',
  key: 'patch'
}, {
  title: 'Rocket iPhone Case',
  price: '$16',
  handle: 'rocket-logo-iphone-case',
  cat: 'acc',
  key: 'iphonecase'
}, {
  title: 'Rocket AirPods Case',
  price: '$31.50',
  handle: 'rocket-logo-airpods-case',
  cat: 'acc',
  key: 'airpods'
}, {
  title: 'Apple Watch Band',
  price: '$49.80',
  handle: 'watch-band-for-apple-watch',
  cat: 'acc',
  key: 'watchband'
}];
export function AstroSwag() {
  const [cat, setCat] = useState<Cat>('all');
  const shown = useMemo(() => PRODUCTS.filter(p => cat === 'all' || p.cat === cat), [cat]);
  return <section id="astroswag" className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-id="element-80" data-mp="wiwsin0jye4w">
      <SectionHeading eyebrow="MERCH" title={<>
            Future <span className="text-gradient" data-id="element-82" data-mp="15leer1g7j6rw">AstroSwag</span>
          </>} description="Gear from the mission — tees, hoodies, caps and prints." data-id="element-81" data-mp="11jsxz50jy8cf" />

      <Reveal data-id="element-83" data-mp="1fafucqiubm5c">
        {/* gradient hairline frame around the shop window */}
        <div className="mt-7 rounded-2xl bg-gradient-to-r from-[#37e5ff55] via-[#8b5cff55] to-[#c86bff55] p-px shadow-[0_0_44px_rgba(139,92,255,0.14)]" data-id="element-84" data-mp="1fciui3dpivns">
          <div className="overflow-hidden rounded-[calc(1rem-1px)] bg-[#07051a]/95" data-id="element-85" data-mp="1p9n9nj94xqv3">
            {/* category tabs */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-86" data-mp="1taqny1bjbeu7">
              {TABS.map(t => <button key={t.key} type="button" onClick={() => setCat(t.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-tech text-[0.56rem] font-semibold tracking-[0.12em] transition-colors duration-200 sm:text-[0.6rem] ${cat === t.key ? 'border-transparent bg-gradient-to-r from-[#37e5ff] via-[#8b5cff] to-[#c86bff] text-[#050414] shadow-neon' : 'border-white/15 text-white/60 hover:border-violet/50 hover:text-white'}`} data-id="element-87" data-mp="3lc0c46x5l7nt">
                  {t.dot && cat !== t.key && <span className="h-1.5 w-1.5 rounded-full" style={{
                background: t.dot
              }} aria-hidden="true" data-id="element-88" data-mp="2nnzzbdal8n1c" />}
                  {t.label}
                </button>)}
              <span className="ml-auto hidden font-tech text-[0.56rem] tracking-[0.14em] text-cyan/50 sm:block" data-id="element-89" data-mp="29ipzu2qp8dag">
                {shown.length} ITEMS
              </span>
            </div>

            {/* dense scrollable grid — cut-out products on glow tiles */}
            <div className="scp-list grid max-h-[47vh] grid-cols-3 gap-2 overflow-y-auto overscroll-contain p-2.5 lg:max-h-[48vh] lg:grid-cols-6 lg:gap-2.5 lg:p-3.5" data-id="element-90" data-mp="1zsem5oh7qpqd">
              {shown.map(p => <a key={p.handle} href={`${STORE_URL}/products/${p.handle}`} target="_blank" rel="noopener noreferrer" className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0a0720] transition-[transform,border-color,box-shadow] duration-200 ease-expo hover:-translate-y-0.5 hover:border-violet/60 hover:shadow-neon" data-id="element-91" data-mp="1pwlb69j574ap">
                  <span className="block aspect-square overflow-hidden" style={{
                background: `radial-gradient(120% 95% at 50% 32%, ${GLOW[p.cat]}, rgba(5,4,20,0) 72%), linear-gradient(180deg, #0d0925 0%, #070517 100%)`
              }} data-id="element-92" data-mp="3g76b1rf5heux">
                    <img src={CLD(p.key)} alt={p.title} loading="lazy" className="h-full w-full object-contain p-2 drop-shadow-[0_12px_16px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-expo group-hover:scale-[1.07]" data-id="element-93" data-mp="iulfkkknqtlz" />
                  </span>
                  <span className="px-2 pt-1.5 font-display text-[0.46rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white lg:text-[0.5rem]" data-id="element-94" data-mp="3w251cqz2k2nl">
                    {p.title}
                  </span>
                  <span className="px-2 pb-1.5 pt-0.5 font-tech text-[0.52rem] text-cyan lg:text-[0.56rem]" data-id="element-95" data-mp="1uaa3be5qjwnu">{p.price}</span>
                </a>)}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15} data-id="element-96" data-mp="386ti30tv0wnl">
        <div className="mt-5 flex justify-center lg:mt-6" data-id="element-97" data-mp="385idwzwi5mvy">
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-violet px-7 py-3.5 font-tech text-xs font-semibold tracking-[0.16em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft" data-id="element-98" data-mp="3d54qr88f5148">
            SHOP ASTROSWAG
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-200 ease-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-99" data-mp="2u7tu7ve68lsh" />
          </a>
        </div>
      </Reveal>
    </section>;
}